import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { GuestRoute } from '@/routes/GuestRoute'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { PermissionRoute } from '@/routes/PermissionRoute'
import { useThemeEffect } from '@/hooks/useThemeEffect'
import { FeatureErrorBoundary } from '@/components/shared/FeatureErrorBoundary'

// Lazy-load all page components
const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const SignupPage = lazy(() => import('@/features/auth/SignupPage'))
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const InboxPage = lazy(() => import('@/features/inbox/InboxPage'))
const CustomersPage = lazy(() => import('@/features/customers/CustomersPage'))
const CustomerDetailPage = lazy(() => import('@/features/customers/CustomerDetailPage'))
const SegmentsPage = lazy(() => import('@/features/segments/SegmentsPage'))
const SegmentDetailPage = lazy(() => import('@/features/segments/SegmentDetailPage'))
const TemplatesPage = lazy(() => import('@/features/templates/TemplatesPage'))
const TemplateBuilderPage = lazy(() => import('@/features/templates/TemplateBuilderPage'))
const CampaignsPage = lazy(() => import('@/features/campaigns/CampaignsPage'))
const CampaignBuilderPage = lazy(() => import('@/features/campaigns/CampaignBuilderPage'))
const CampaignDetailPage = lazy(() => import('@/features/campaigns/CampaignDetailPage'))
const FlowsPage = lazy(() => import('@/features/flows/FlowsPage'))
const FlowBuilderPage = lazy(() => import('@/features/flows/FlowBuilderPage'))
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'))
const KnowledgeBasePage = lazy(() => import('@/features/knowledge-base/KnowledgeBasePage'))
const AIChatbotPage = lazy(() => import('@/features/ai-chatbot/AIChatbotPage'))
const KnowledgeBaseDetailPage = lazy(() => import('@/features/knowledge-base/KnowledgeBaseDetailPage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))
const EcommercePage = lazy(() => import('@/features/ecommerce/EcommercePage'))
const CatalogPage = lazy(() => import('@/features/catalog/CatalogPage'))
const IntegrationsPage = lazy(() => import('@/features/integrations/IntegrationsPage'))
const ImportExportPage = lazy(() => import('@/features/import-export/ImportExportPage'))
const PipelinePage = lazy(() => import('@/features/pipeline/PipelinePage'))
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'))
const WhatsAppFlowsPage = lazy(() => import('@/features/wa-flows/WhatsAppFlowsPage'))
const WAFlowBuilderPage = lazy(() => import('@/features/wa-flows/WAFlowBuilderPage'))
const BillingPage = lazy(() => import('@/features/billing/pages/BillingPage'))
const BillingProfilePage = lazy(() => import('@/features/billing/pages/BillingProfilePage'))
const HelpCenterPage = lazy(() => import('@/features/help/HelpCenterPage'))
const CsatPage = lazy(() => import('@/features/csat/CsatPage'))
const ApiActivityPage = lazy(() => import('@/features/api-activity/ApiActivityPage'))
const MediaLibraryPage = lazy(() => import('@/features/media-library/MediaLibraryPage'))

/** Old /help/:slug links (bookmarks, shared URLs) → the renamed Documents page. */
function HelpSlugRedirect() {
  const { slug } = useParams<{ slug: string }>()
  return <Navigate to={slug ? `/documents/${slug}` : '/documents'} replace />
}

/** Minimal full-screen loader for public routes (login/signup) */
function AuthPageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

export default function App() {
  useThemeEffect()

  return (
    <Routes>
      {/* Public routes — own Suspense so they don't affect the app shell */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <Suspense fallback={<AuthPageLoader />}>
              <LoginPage />
            </Suspense>
          </GuestRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <GuestRoute>
            <Suspense fallback={<AuthPageLoader />}>
              <SignupPage />
            </Suspense>
          </GuestRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<AuthPageLoader />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />

      {/* Protected routes — Suspense is inside AppLayout so sidebar/header stay mounted */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* No special permission required */}
        <Route path="/dashboard" element={<FeatureErrorBoundary featureName="Dashboard"><DashboardPage /></FeatureErrorBoundary>} />

        <Route path="/inbox" element={<PermissionRoute permissions={['view_inbox']}><FeatureErrorBoundary featureName="Inbox"><InboxPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/inbox/:conversationId" element={<PermissionRoute permissions={['view_inbox']}><FeatureErrorBoundary featureName="Inbox"><InboxPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/customers" element={<PermissionRoute permissions={['view_customers']}><FeatureErrorBoundary featureName="Customers"><CustomersPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/customers/:customerId" element={<PermissionRoute permissions={['view_customers']}><FeatureErrorBoundary featureName="Customer Detail"><CustomerDetailPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/segments" element={<PermissionRoute permissions={['view_customersegment']}><FeatureErrorBoundary featureName="Segments"><SegmentsPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/segments/:segmentId" element={<PermissionRoute permissions={['view_customersegment']}><FeatureErrorBoundary featureName="Segment Detail"><SegmentDetailPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/templates" element={<PermissionRoute permissions={['view_templates']}><FeatureErrorBoundary featureName="Templates"><TemplatesPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/templates/create" element={<PermissionRoute permissions={['create_templates']} redirectTo="/templates" deniedMessage="You don't have permission to create templates."><FeatureErrorBoundary featureName="Template Builder"><TemplateBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />
        {/* Alias — "/templates/new" would otherwise match :templateId and load template "new". */}
        <Route path="/templates/new" element={<Navigate to="/templates/create" replace />} />
        <Route path="/templates/:templateId" element={<PermissionRoute permissions={['view_templates']}><FeatureErrorBoundary featureName="Template Builder"><TemplateBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/templates/:templateId/edit" element={<PermissionRoute permissions={['view_templates']}><FeatureErrorBoundary featureName="Template Builder"><TemplateBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/campaigns" element={<PermissionRoute permissions={['view_campaigns']}><FeatureErrorBoundary featureName="Campaigns"><CampaignsPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/campaigns/create" element={<PermissionRoute permissions={['create_campaigns']} redirectTo="/campaigns" deniedMessage="You don't have permission to create campaigns."><FeatureErrorBoundary featureName="Campaign Builder"><CampaignBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/campaigns/:type/:id/edit" element={<PermissionRoute permissions={['edit_campaigns']} redirectTo="/campaigns" deniedMessage="You don't have permission to edit campaigns."><FeatureErrorBoundary featureName="Campaign Builder"><CampaignBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/campaigns/:campaignType/:campaignId" element={<PermissionRoute permissions={['view_campaigns']}><FeatureErrorBoundary featureName="Campaign Detail"><CampaignDetailPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/campaigns/:campaignId" element={<PermissionRoute permissions={['view_campaigns']}><FeatureErrorBoundary featureName="Campaign Detail"><CampaignDetailPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/activity-log" element={<PermissionRoute permissions={['view_apiactivitylog']}><FeatureErrorBoundary featureName="API Activity"><ApiActivityPage /></FeatureErrorBoundary></PermissionRoute>} />
        {/* Setu Media library. Not "/media…" — the dev server proxies /media to Django. */}
        <Route path="/setu-media" element={<PermissionRoute permissions={['view_media_library']}><FeatureErrorBoundary featureName="Setu Media"><MediaLibraryPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/flows" element={<PermissionRoute permissions={['view_flows']}><FeatureErrorBoundary featureName="Flows"><FlowsPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/flows/:flowId/builder" element={<PermissionRoute permissions={['view_flows']}><FeatureErrorBoundary featureName="Flow Builder"><FlowBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/analytics" element={<PermissionRoute permissions={['view_analytics']}><FeatureErrorBoundary featureName="Analytics"><AnalyticsPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/knowledge-base" element={<PermissionRoute permissions={['view_knowledge_base']}><FeatureErrorBoundary featureName="Knowledge Base"><KnowledgeBasePage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/knowledge-base/:kbId" element={<PermissionRoute permissions={['view_knowledge_base']}><FeatureErrorBoundary featureName="Knowledge Base"><KnowledgeBaseDetailPage /></FeatureErrorBoundary></PermissionRoute>} />

        <Route path="/ai-chatbot" element={<PermissionRoute permissions={['view_chatbot']}><FeatureErrorBoundary featureName="AI Chatbot"><AIChatbotPage /></FeatureErrorBoundary></PermissionRoute>} />

        {/* Live Chat was removed — hand-over now lives in the Inbox. */}
        <Route path="/live-chat" element={<Navigate to="/inbox" replace />} />

        {/* Settings and notifications: no special permission (sub-permissions handled within) */}
        <Route path="/settings" element={<FeatureErrorBoundary featureName="Settings"><SettingsPage /></FeatureErrorBoundary>} />
        <Route path="/settings/:tab" element={<FeatureErrorBoundary featureName="Settings"><SettingsPage /></FeatureErrorBoundary>} />

        <Route path="/notifications" element={<FeatureErrorBoundary featureName="Notifications"><NotificationsPage /></FeatureErrorBoundary>} />

        {/* Documents (formerly Help) — in-app product guides, gated on view_documents */}
        <Route path="/documents" element={<PermissionRoute permissions={['view_documents']}><FeatureErrorBoundary featureName="Documents"><HelpCenterPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/documents/:slug" element={<PermissionRoute permissions={['view_documents']}><FeatureErrorBoundary featureName="Documents"><HelpCenterPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/help" element={<Navigate to="/documents" replace />} />
        <Route path="/help/:slug" element={<HelpSlugRedirect />} />
        <Route path="/pipeline" element={<PermissionRoute permissions={['view_pipeline']}><FeatureErrorBoundary featureName="Pipeline"><PipelinePage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/import-export" element={<PermissionRoute permissions={['import_customers', 'export_customers']}><FeatureErrorBoundary featureName="Import/Export"><ImportExportPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/ecommerce" element={<PermissionRoute permissions={['view_ecommerce']}><FeatureErrorBoundary featureName="E-commerce"><EcommercePage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/catalog" element={<PermissionRoute permissions={['view_templates']}><FeatureErrorBoundary featureName="Catalog"><CatalogPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/csat" element={<PermissionRoute permissions={['view_csat']}><FeatureErrorBoundary featureName="CSAT"><CsatPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/integrations" element={<PermissionRoute permissions={['view_integrations']}><FeatureErrorBoundary featureName="Integrations"><IntegrationsPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/wa-flows" element={<PermissionRoute permissions={['view_whatsapp_flows']}><FeatureErrorBoundary featureName="WhatsApp Flows"><WhatsAppFlowsPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/wa-flows/:flowId/builder" element={<PermissionRoute permissions={['view_whatsapp_flows']}><FeatureErrorBoundary featureName="WA Flow Builder"><WAFlowBuilderPage /></FeatureErrorBoundary></PermissionRoute>} />

        {/* Phase 7 — billing redesign. /subscription redirects here.
            Gated on view_billing to match the sidebar and the API: the menu
            entry was already hidden without it, so reaching the page meant
            typing the URL. */}
        <Route path="/billing" element={<PermissionRoute permissions={['view_billing']}><FeatureErrorBoundary featureName="Billing"><BillingPage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/settings/billing-profile" element={<PermissionRoute permissions={['view_billing']}><FeatureErrorBoundary featureName="Billing Profile"><BillingProfilePage /></FeatureErrorBoundary></PermissionRoute>} />
        <Route path="/subscription" element={<Navigate to="/billing" replace />} />
        <Route path="/subscription/*" element={<Navigate to="/billing" replace />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
